// Copyright 2019 Sorama B.V.

import {
    autoinject,
    bindable,
    bindingMode,
    computedFrom,
    containerless,
    customElement,
    Disposable,
} from "aurelia-framework";
import { Quad } from "datatypes/Generic/Quad";
import { Point2D } from "datatypes/SoramaLocalApi";
import { GestureListener } from "libraries/Layouting/GestureListener";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { CanvasRegion, CanvasRegionDrawParent, MovementMode } from "./CanvasRegion";

@autoinject
@containerless
@customElement("image-region-picker")
export class ImageRegionPicker implements CanvasRegionDrawParent {
    //#region Bindables
    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public src: string;

    @bindable({ defaultBindingMode: bindingMode.twoWay, defaultValue: Quad.unitQuad })
    public regionOfInterest: Quad;

    @bindable({ defaultBindingMode: bindingMode.oneWay, defaultValue: 1 })
    public deviceAspectRatio: number;
    //#endregion

    public movementMode: MovementMode = MovementMode.Both;

    @computedFrom("movementMode")
    public get movementIcon(): string {
        switch (this.movementMode) {
            case MovementMode.Horizontal:
                return "fa fa-arrows-alt-h";
            case MovementMode.Vertical:
                return "fa fa-arrows-alt-v";
            default:
                return "fa fa-arrows-alt";
        }
    }

    private get imageLoaded(): boolean {
        return !(!this.imageElement || this.imageElement.width === 0 || this.imageElement.height === 0);
    }

    private get scale(): number {
        if (!this.imageLoaded) return 1;
        return (
            this.zoomLevel *
            Math.min(
                this.canvasElement.width / this.imageElement.width,
                this.canvasElement.height / this.imageElement.height
            )
        );
    }

    @computedFrom("imageLoaded", "imageElement")
    public get imageAspectRatio(): number {
        if (!this.imageLoaded) {
            return this.deviceAspectRatio;
        }

        return this.imageElement.width / this.imageElement.height;
    }

    public readonly containerElement: HTMLDivElement;
    public readonly canvasElement: HTMLCanvasElement;

    private readonly disposables: Disposable[] = [];

    private gestureListener: GestureListener;
    private context: CanvasRenderingContext2D;
    private canvasRegion: CanvasRegion;
    private initialRegion: Quad;

    private currentlyLoadedImage: string;
    private imageElement: HTMLImageElement;
    private imageLoadPromise: Promise<void>;

    private zoomLevel = 1;
    private panX = 0;
    private panY = 0;

    constructor(private readonly resizeListener: ResizeListener) {
        this.imageLoadPromise = Promise.resolve();
    }

    public bind(): void {
        if (!this.containerElement || !this.canvasElement) {
            throw new Error("Unable to load control, missing view elements");
        }

        this.context = this.canvasElement.getContext("2d");
        this.imageElement = document.createElement("img");

        // Add mouse gesture and resize listeners
        this.gestureListener = new GestureListener(this.canvasElement);

        this.disposables.push(this.gestureListener.zoomListener(this.onZoom));
        this.disposables.push(this.gestureListener.panListener(this.onPan));
        this.disposables.push(this.resizeListener.elementResizeEvent(this.containerElement, this.resize));

        if (this.src) {
            // Trigger this manually
            this.srcChanged();
        }

        if (this.regionOfInterest) {
            // Trigger this manually
            this.regionOfInterestChanged();
            this.initialRegion = Quad.clone(this.regionOfInterest);
        }
    }

    public unbind(): void {
        while (this.disposables.length > 0) {
            this.disposables.pop().dispose();
        }

        if (this.canvasRegion) {
            this.canvasRegion.dispose();
            this.canvasRegion = undefined;
        }

        // Set to undefined to help the garbage collector a bit?
        this.imageElement = undefined;
        this.gestureListener = undefined;
    }

    //#region CanvasRegionDrawParent implementation

    public postRedraw(): void {
        window.requestAnimationFrame(this.redraw);
    }

    public toRegionCoordinate(p: Point2D): Point2D {
        return {
            x: (p.x - this.panX) / this.scale / this.imageElement.width,
            y: (p.y - this.panY) / this.scale / this.imageElement.height,
        };
    }

    public toViewCoordinate(p: Point2D): Point2D {
        return {
            x: p.x * this.scale * this.imageElement.width + this.panX,
            y: p.y * this.scale * this.imageElement.height + this.panY,
        };
    }

    //#endregion

    //#region Update methods
    private resize = () => {
        if (
            this.canvasElement.width === this.containerElement.clientWidth &&
            this.canvasElement.height === this.containerElement.clientHeight
        ) {
            // No need to resize
            return;
        }

        this.canvasElement.width = this.containerElement.clientWidth;
        this.canvasElement.height = this.containerElement.clientHeight;

        // Make sure the region is in the viewport
        this.panZoomToRegion();

        // Resize complete, also do a redraw
        this.redraw();
    };

    private redraw = () => {
        if (!this.imageLoaded) {
            // No element, no redraw
            return;
        }

        this.context.save();

        // Determine where the image should be drawn
        this.context.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);

        this.context.translate(this.panX, this.panY);
        this.context.scale(this.scale * this.imageElement.width, this.scale * this.imageElement.height);
        this.context.drawImage(this.imageElement, 0, 0, 1, 1);

        // Restore the context to original since drawing lines with scaling properties set results in wierd effects.
        this.context.restore();

        if (this.canvasRegion) {
            this.canvasRegion.draw(this.context);
        }
    };

    private panZoomToRegion(): void {
        if (!this.imageLoaded) return;

        // Determine rectangular bounding box of the current region
        const left =
            Math.min(
                this.regionOfInterest.topLeft.x,
                this.regionOfInterest.topRight.x,
                this.regionOfInterest.bottomRight.x,
                this.regionOfInterest.bottomLeft.x
            ) * this.imageElement.width;

        const right =
            Math.max(
                this.regionOfInterest.topLeft.x,
                this.regionOfInterest.topRight.x,
                this.regionOfInterest.bottomRight.x,
                this.regionOfInterest.bottomLeft.x
            ) * this.imageElement.width;

        const top =
            Math.min(
                this.regionOfInterest.topLeft.y,
                this.regionOfInterest.topRight.y,
                this.regionOfInterest.bottomRight.y,
                this.regionOfInterest.bottomLeft.y
            ) * this.imageElement.height;

        const bottom =
            Math.max(
                this.regionOfInterest.topLeft.y,
                this.regionOfInterest.topRight.y,
                this.regionOfInterest.bottomRight.y,
                this.regionOfInterest.bottomLeft.y
            ) * this.imageElement.height;

        const scale = Math.min(
            this.canvasElement.width / this.imageElement.width,
            this.canvasElement.height / this.imageElement.height
        );

        // Determine zoom level and pan
        const width = (right - left) * scale;
        const height = (bottom - top) * scale;
        const zoom = Math.min(this.canvasElement.width / width, this.canvasElement.height / height) * 0.9;

        this.gestureListener.reset(zoom, {
            x: 0.5 * (this.canvasElement.width - width * zoom) - left * scale * zoom,
            y: 0.5 * (this.canvasElement.height - height * zoom) - top * scale * zoom,
        });
    }

    //#endregion

    //#region Gesture callbacks

    private onZoom = (zoom: number): number => {
        zoom = Math.max(Math.min(zoom, 10), 0.1);
        this.zoomLevel = zoom;

        // Redraw async
        this.postRedraw();

        return zoom;
    };

    private onPan = (pan: Point2D): Point2D => {
        this.panX = Math.min(Math.max(pan.x, -this.canvasElement.width * this.zoomLevel), this.canvasElement.width);
        this.panY = Math.min(Math.max(pan.y, -this.canvasElement.height * this.zoomLevel), this.canvasElement.height);

        // Redraw async
        this.postRedraw();

        return <Point2D>{ x: this.panX, y: this.panY };
    };

    //#endregion

    //#region Button callbacks

    public resetRegion(): void {
        if (this.initialRegion) {
            this.regionOfInterest = Quad.clone(this.initialRegion);
            this.panZoomToRegion();
        } else {
            this.fitImage();
        }
    }

    public fitRegion(): void {
        if (!this.imageLoaded) {
            return;
        }

        // Compute new quad that fits image
        const imageRegion = Quad.fitQuadInArea(this.deviceAspectRatio, this.imageElement);

        // Normalize image region
        this.regionOfInterest = Quad.scale(imageRegion, 1 / this.imageElement.width, 1 / this.imageElement.height);

        // Zoom to fit region
        this.panZoomToRegion();
    }

    public fitImage(): void {
        if (!this.imageLoaded) {
            return;
        }

        // Compute new quad that contains the image
        const imageRegion = Quad.fitQuadOverArea(this.deviceAspectRatio, this.imageElement);

        // Normalize image region
        this.regionOfInterest = Quad.scale(imageRegion, 1 / this.imageElement.width, 1 / this.imageElement.height);

        // Zoom to fit region
        this.panZoomToRegion();
    }

    public toggleMoveMode(): void {
        this.movementMode = (this.movementMode + 1) % 3;
    }

    //#endregion

    //#region Change handlers

    public srcChanged(): void {
        // Load new image
        this.imageLoadPromise = this.imageLoadPromise.then(() => {
            // Check if we need to load a new image
            if (this.currentlyLoadedImage === this.src) {
                return;
            }

            // Load new image
            const imageToLoad = this.src;

            let onLoadComplete: () => void;
            let onLoadError: () => void;

            return new Promise<void>((resolve, reject) => {
                onLoadComplete = () => {
                    resolve();
                };

                onLoadError = () => {
                    reject();
                };

                this.imageElement.addEventListener("load", onLoadComplete);
                this.imageElement.addEventListener("error", onLoadError);
                this.imageElement.src = imageToLoad;
            })
                .then(() => {
                    // Set current image
                    this.currentlyLoadedImage = imageToLoad;

                    // Image loaded, resize the control to match
                    this.resize();
                })
                .catch(() => {
                    // No image, ignore this for now?
                })
                .then(() => {
                    // make sure to unset event listeners
                    this.imageElement.removeEventListener("load", onLoadComplete);
                    this.imageElement.removeEventListener("error", onLoadError);
                });
        });
    }

    public regionOfInterestChanged(): void {
        if (this.canvasRegion) {
            this.canvasRegion.dispose();
            this.canvasRegion = undefined;
        }

        if (this.regionOfInterest) {
            this.canvasRegion = new CanvasRegion(this.canvasElement, this);
        }

        if (!this.initialRegion) {
            this.initialRegion = this.regionOfInterest;
        }

        this.redraw();
    }

    //#endregion
}
